import WasmController from "react-lib/frameworks/WasmController";

// Interface
import { State as MainState } from "../../../../../HIS/MainHISInterface";

// Common
import { SetProperty } from "../../common/CommonInterface";
import { getCompanyFont } from "react-lib/apps/HISV3/common/CompanyLogoForm";
import moment from "moment";
import getPdfMake from "react-lib/appcon/common/pdfMake";
import { PDFDocument } from "pdf-lib";
import { ContentStack, TDocumentDefinitions } from "pdfmake/interfaces";

// Utils
import { dateToStringWithoutTimeBE } from "react-lib/utils/dateUtils";

// API

// PDF Header
import {
  // header
  HeaderLogoWithAddress,
  HeaderLogoWithTitleContent,
  HeaderLogoWithPublishDetail,
  HeaderLogoWithPatientDetailImage,
  HeaderPatientDetail,
  HeaderLogoWithPatientDetail,
  HeaderPrintList,
  HeaderPrintListLandscape,
  HeaderNonLogoWithPatientDetailImage,
  HeaderOperativeSchedule,
  HeaderSummaryInvoiceIpd,
  // props
  CommonProps,
  BaseHeaderDetail,
} from "react-lib/apps/HISV3/common/HeaderPdfFormTemplate";

export type State = Partial<{
  // seq
  PdfMakeHeaderPreviewSequence: Partial<{
    sequenceIndex: "Start" | "Action" | null;
    options: any; // ยังคิดไม่ออก
  }> | null;
}>;

type PickedState = Partial<Pick<MainState, "buttonLoadCheck" | "errorMessage" | "successMessage">>;

export type PickedProps = Partial<Omit<PickedState, "">>;

// Sequence
type SeqState = {
  sequence: "PdfMakeHeaderPreview";
  restart?: boolean;
  clear?: boolean;
};

// Handle Action
type ActionType = 
  | { action: "PREVIEW"; }
  | { action: "TEST" };

type SeqAct = ActionType & SeqState;

type SeqType<K> = K extends { action: string } ? Extract<SeqAct, K> : SeqState;

export type RunSequence = <K extends keyof SeqAct>(params: SeqType<Pick<SeqAct, K>>) => void;

export type SetProp = SetProperty<State & PickedState>;

type CustomExtract<T, U> = T extends object ? (U extends Partial<T> ? T : never) : never;

type Params<A extends ActionType["action"]> = CustomExtract<ActionType, { action: A }>;

export const StateInitial: State & PickedState = {
  // seq
  PdfMakeHeaderPreviewSequence: {
    sequenceIndex: null,
  },
  // SnackMessage
  successMessage: null,
  errorMessage: null,
};

export type Event = { message: "RunSequence"; params: {} };

export type Data = {
  division?: number;
};

export const DataInitial = {};

export const CARD_NAME = "CardPdfMakeHeaderPreview";

type Optional<T> = {
  [K in keyof T]: `${typeof CARD_NAME}_${T[K] & string}`;
};

type Handler<P = any, R = any> = (
  controller: WasmController<State & PickedState, Event, Data>,
  params: P
) => R;

// type HeaderDataType = {
//   font: string | undefined;
//   fontSizes: Record<number, number>;
//   lineHeights: Record<number, number>;
//   pageMargins: TDocumentDefinitions["pageMargins"];
//   styles: TDocumentDefinitions["styles"];
//   images?: Record<string, string>;
//   header: () => any;
// };

// type HeaderComponentFn<T extends CommonProps = CommonProps> = (props: T) => Promise<HeaderDataType>;

// type HeaderTemplateConfig<T extends CommonProps = CommonProps> = {
//   component: HeaderComponentFn<T>;
//   name: string;
//   extraProps?: Partial<T>;
//   pageOrientation?: "landscape" | "portrait";
//   overrideMargins?: TDocumentDefinitions["pageMargins"];
//   overrideFontSize?: Record<number, number>;
// };

type OptionalProps = Partial<{
  formName: string;
  detail: BaseHeaderDetail & any;
  // Form #2
  titleContent: ContentStack["stack"];
  // Form #3
  publishContent: ContentStack["stack"];
  haveBorder: boolean;
  isHospitalVertical: boolean;
  content: ContentStack["stack"];
}>;

export const ACTIONS = {
  PREVIEW: "PREVIEW",
  TEST: "TEST",
} as const;

export const BTN_ACTS = Object.fromEntries(
  Object.keys(ACTIONS).map((key) => [key, `${CARD_NAME}_${key}`])
) as Optional<typeof ACTIONS>;

/**========================================================================
 **                               START
 *========================================================================**/

export const Start: Handler<SeqState> = async (controller, params) => {
  const state = controller.getState();

  const currentDate = dateToStringWithoutTimeBE(moment());

  controller.setState(
    {
      PdfMakeHeaderPreviewSequence: {
        ...state.PdfMakeHeaderPreviewSequence,
        sequenceIndex: "Action",
        options: { createdDate: currentDate },
      },
    },
    () => Action(controller, { ...params, action: "PREVIEW" }),
  );
};

/**========================================================================
 **                              ACTIONS
 *========================================================================**/

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers = {
    [ACTIONS.PREVIEW]: HandlePreviewPdf,
    [ACTIONS.TEST]: HandleTestPdf,
  };

  const action = params.action;

  if (action && actionHandlers[action]) {
    return actionHandlers[action](controller, params as any);
  }
};

const HandlePreviewPdf: Handler<Params<"PREVIEW">> = async (controller, params) => {
  const state = controller.getState();

  const pdfBase64 = await CreatePdfAllHeader(controller, params);

  controller.setState({
    PdfMakeHeaderPreviewSequence: {
      ...state.PdfMakeHeaderPreviewSequence,
      options: {
        ...state.PdfMakeHeaderPreviewSequence?.options,
        pdfBase64,
      }
    }
  });
};

const HandleTestPdf: Handler<Params<"TEST">> = async (controller, params) => {
  const state = controller.getState();

  // console.log("Yeti [HandleTestPDf]");
};

/**========================================================================
 **                              PDF FUNC
 *========================================================================**/

const CreatePdfAllHeader: Handler = async (contoller, params) => {
  const state = contoller.getState();

  const currentDate = dateToStringWithoutTimeBE(moment());

  const headerList = [
    HeaderLogoWithAddress,
    HeaderLogoWithTitleContent,
    HeaderLogoWithPublishDetail,
    HeaderLogoWithPatientDetailImage,
    HeaderPatientDetail,
    HeaderLogoWithPatientDetail,
    HeaderPrintList,
    HeaderPrintListLandscape,
    HeaderNonLogoWithPatientDetailImage,
    // HeaderOperativeSchedule,
    HeaderSummaryInvoiceIpd,
  ];

  const detailMockup: BaseHeaderDetail & any = {
    divisionName: "divisionName",
    doctorName: "doctorName",
    hn: "hn",
    number: "number",
    patient_age: "patient_age",
    patient_birthdate: "patient_birthdate",
    patient_gender_name: "patient_gender",
    patientName: "patientName",
    telMobile: "telMobile",
    // optional (Form #4)
    address: "address",
    citizen_no: "citizen_no",
    citizen_passport: "citizen_passport",
    profileImage: null,
    started: currentDate,
    // Form #7
    doctor_number: "doctor_number",
    // Form #8
    visit_date: "visit_date",
    allergies: "allergies",
    ipd_room: "ipd_room",
    // Form #9
    patient_age_split: ["25", "8", "12"],
    coverage: "coverage",
  };

  const optionalProps: OptionalProps = {
    titleContent: [
      {
        text: [
          { text: `Header Template Title\n`, style: { fontSize: 18, bold: true }},
          { text: `This is "titleContent" Section`, style: { fontSize: 18 }},
        ],
        alignment: "center",
      },
    ],
    publishContent: [
      { 
        text: [
          { text: "ผู้พิมพ์: ", bold: true, preserveLeadingSpaces: true },
          { text: "นายทดสอบ Header" },
        ],
        alignment: "right",
        fontSize: 12,
      },
      {
        text: [
          { text: "วันที่พิมพ์: ", bold: true, preserveLeadingSpaces: true },
          { text: currentDate },
        ],
        alignment: "right",
        fontSize: 12,
      },
    ],
    haveBorder: true,
    isHospitalVertical: true,
    content: [
      { text: `"content" Section` }
    ],
  };

  const baseProps: CommonProps & OptionalProps = {
    font: getCompanyFont(),
    form: "PDFMakeHeaderPreview",
    formName: "PDFMake Header Preview",
    // headerMargins: [15, 15, 15, 0],
    pageMargins: [15, 120, 15, 20],
    detail: detailMockup,
    ...optionalProps,
  };

  const documents = [];

  let index = 1;

  for (const header of headerList) {
    try {
      const headerRow = header.name;
      const headerData = await header({
        ...baseProps as any,
        formName: `#${index} ${headerRow}`
      });
      const docDef: any = {
        pageSize: "A4",
        ...(header === headerList[7] ? { pageOrientation: "landscape" } : {}),
        ...(header === headerList[8] ? { pageMargins: [15, 160, 15, 0] } : { pageMargins: headerData.pageMargins }),
        defaultStyle: {
          font: headerData.font,
          fontSize: header === headerList[8] ? headerData.fontSizes[14] : headerData.fontSizes[12],
          lineHeight: headerData.lineHeights[1],
        },
        styles: headerData.styles,
        images: (headerData as any).images,
        header: headerData.header,
        content: [
          { text: `Header #${index} ${headerRow}`, alignment: "center", bold: true }
        ],
      };

      const pdfMakeIns = await getPdfMake(true);

      const base64 = await new Promise<string>((resolve, reject) => {
        try {
          const pdfMake = pdfMakeIns.createPdf(docDef);
          pdfMake.getBase64((data) => resolve(data));
        } catch (err) {
          reject(err);
        }
      });

      documents.push(base64);

      index++;
    } catch (err) {
      const headerRow = header.name;
      console.error(`Yeti Error Generating PDF: ${headerRow}`, err);
    }
  }

  const mergedBase64 = await mergePdfBase64List(documents);

  return mergedBase64 || "";
};

/**========================================================================
 **                              UTILS
 *========================================================================**/

const base64ToUint8Array = (base64: string): Uint8Array => {
  const binString = atob(base64);
  const len = binString.length;
  const bytes = new Uint8Array(len);

  for (let i = 0; i < len; i++) {
    bytes[i] = binString.charCodeAt(i);
  }

  return bytes;
};

const mergePdfBase64List = async (base64List: string[]): Promise<string> => {
  const mergedPdf = await PDFDocument.create();

  for (const base64 of base64List) {
    const pdfBytes = base64ToUint8Array(base64);
    const pdf = await PDFDocument.load(pdfBytes);
    const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
    copiedPages.forEach((page) => mergedPdf.addPage(page));
  }

  const mergedPdfBytes = await mergedPdf.save();

  return Buffer.from(mergedPdfBytes).toString("base64");
};